import React, { useEffect, useState } from 'react';
import { fetchStaffFirestore, addStaffFirestore, updateStaffFirestore, deleteStaffFirestore } from '../../api/fff';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

const AdminStaff = () => {
  const [equipes, setEquipes] = useState([]);
  const [selectedEquipe, setSelectedEquipe] = useState('');
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ nom: '', prenom: '', fonction: '', equipe: '' });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Charger la liste des équipes Firestore
  useEffect(() => {
    const fetchEquipes = async () => {
      const snap = await getDocs(collection(db, 'equipes'));
      setEquipes(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchEquipes();
  }, []);

  // Charger le staff de l'équipe sélectionnée
  useEffect(() => {
    if (!selectedEquipe) return;
    setLoading(true);
    setError(null);
    fetchStaffFirestore(selectedEquipe)
      .then(data => {
        setStaff(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Erreur lors du chargement du staff.');
        setLoading(false);
      });
  }, [selectedEquipe]);

  // Gérer le formulaire (ajout/édition)
  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.nom || !form.prenom || !form.fonction || !selectedEquipe) return;
    setSaving(true);
    try {
      if (editId) {
        await updateStaffFirestore(editId, { ...form, equipe: selectedEquipe });
      } else {
        await addStaffFirestore({ ...form, equipe: selectedEquipe });
      }
      setForm({ nom: '', prenom: '', fonction: '', equipe: '' });
      setEditId(null);
      // Refresh
      const data = await fetchStaffFirestore(selectedEquipe);
      setStaff(data);
    } catch {
      setError('Erreur lors de la sauvegarde.');
    }
    setSaving(false);
  };

  const handleEdit = m => {
    setForm({ nom: m.nom, prenom: m.prenom, fonction: m.fonction, equipe: selectedEquipe });
    setEditId(m.id);
  };

  const handleDelete = async id => {
    if (!window.confirm('Supprimer ce membre du staff ?')) return;
    setSaving(true);
    await deleteStaffFirestore(id);
    const data = await fetchStaffFirestore(selectedEquipe);
    setStaff(data);
    setSaving(false);
  };

  return (
    <div className="max-w-3xl mx-auto mt-8">
      <h1 className="text-3xl font-extrabold text-[#FFE400] mb-6 text-center">Gestion du staff</h1>
      {/* Sélecteur d'équipe */}
      <div className="mb-6 flex flex-col md:flex-row gap-4 items-center justify-center">
        <label className="font-bold text-lg text-white">Équipe :</label>
        <select
          className="bg-[#00238D] text-[#FFE400] font-bold rounded-xl px-4 py-2 text-lg"
          value={selectedEquipe}
          onChange={e => { setSelectedEquipe(e.target.value); setForm({ nom: '', prenom: '', fonction: '', equipe: '' }); setEditId(null); }}
        >
          <option value="">Sélectionner une équipe</option>
          {equipes.map(eq => (
            <option key={eq.id} value={eq.id}>{eq.nom}</option>
          ))}
        </select>
      </div>
      {/* Formulaire ajout/édition */}
      {selectedEquipe && (
        <form onSubmit={handleSubmit} className="bg-[#001B56] rounded-xl p-6 mb-8 flex flex-col gap-4 shadow-lg">
          <div className="flex flex-col md:flex-row gap-4">
            <input name="prenom" value={form.prenom} onChange={handleChange} placeholder="Prénom" className="flex-1 px-4 py-2 rounded bg-[#00238D] text-white" required />
            <input name="nom" value={form.nom} onChange={handleChange} placeholder="Nom" className="flex-1 px-4 py-2 rounded bg-[#00238D] text-white" required />
            <input name="fonction" value={form.fonction} onChange={handleChange} placeholder="Fonction (ex: U18 - Éducateur)" className="flex-1 px-4 py-2 rounded bg-[#00238D] text-white" required />
          </div>
          <div className="flex gap-4 mt-2">
            <button type="submit" className="bg-[#FFE400] text-[#001B56] font-bold px-6 py-2 rounded-xl shadow hover:scale-105 transition" disabled={saving}>{editId ? 'Modifier' : 'Ajouter'}</button>
            {editId && (
              <button type="button" className="bg-[#00238D] text-white font-bold px-6 py-2 rounded-xl shadow hover:scale-105 transition" onClick={() => { setForm({ nom: '', prenom: '', fonction: '', equipe: '' }); setEditId(null); }}>Annuler</button>
            )}
          </div>
        </form>
      )}
      {/* Liste du staff */}
      {loading ? (
        <div className="text-center text-[#FFE400]">Chargement…</div>
      ) : error ? (
        <div className="text-center text-red-400">{error}</div>
      ) : (
        <ul className="space-y-4">
          {staff.map(m => (
            <li key={m.id} className="flex flex-col md:flex-row md:items-center justify-between bg-[#00238D] rounded-xl p-4 shadow border-l-4 border-[#FFE400]">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <span className="text-lg font-bold text-white uppercase">{m.prenom} {m.nom}</span>
                <span className="text-xs text-[#FFE400]">{m.fonction}</span>
              </div>
              <div className="flex gap-2 mt-2 md:mt-0">
                <button onClick={() => handleEdit(m)} className="bg-[#FFE400] text-[#001B56] font-bold px-4 py-1 rounded-xl shadow hover:scale-105 transition">Éditer</button>
                <button onClick={() => handleDelete(m.id)} className="bg-red-500 text-white font-bold px-4 py-1 rounded-xl shadow hover:scale-105 transition">Supprimer</button>
              </div>
            </li>
          ))}
          {staff.length === 0 && (
            <li className="text-center text-[#FFE400] font-bold">Aucun membre du staff pour cette équipe.</li>
          )}
        </ul>
      )}
    </div>
  );
};

export default AdminStaff; 